import { rememberCanonicalPayload, readCanonicalRoute } from "../../../../lib/copy/src/public-canonical-client.mjs";
import { decodeLandingSeed } from "./compact-landing-seed.mjs";

const apiBase = `${import.meta.env.BASE_URL || "/"}api`;
let installed = false;
const warned = new Set<string>();
function projectionWarning(reason: string) {
  if (warned.has(reason)) return;
  warned.add(reason);
  console.warn("[public-canonical] Optional projection unavailable", { reason });
}
/** One deadline covers headers and JSON parsing, even when fetch ignores abort. */
async function optionalProjection(path: string, budgetMs = 2500): Promise<any | null> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(path, { signal: controller.signal });
        if (!response.ok) throw Error(`http-${response.status}`);
        const data = await response.json();
        if (!controller.signal.aborted) rememberCanonicalPayload(data);
        return data;
      })(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => { controller.abort(); reject(Error("deadline")); }, budgetMs);
      }),
    ]);
  } catch (error) {
    projectionWarning(error instanceof Error ? error.message : "network");
    return null;
  } finally {
    clearTimeout(timer);
  }
}
/**
 * Observe the existing API JSON read boundary, including bespoke profile/trip
 * readers that map DTOs before calling link helpers. No extra HTTP requests,
 * credentials, or payload fields are introduced here.
 */
export function installCanonicalApiTransport() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    let isApi = false;
    try {
      const url = new URL(raw, window.location.href);
      isApi = url.origin === window.location.origin &&
        (url.pathname === apiBase || url.pathname.startsWith(`${apiBase}/`));
    } catch { /* Non-URL requests retain the original transport behavior. */ }
    if (isApi && response.ok) {
      const json = response.json.bind(response);
      response.json = async () => {
        const body = await json();
        rememberCanonicalPayload(body);
        return body;
      };
    }
    return response;
  };
}
/** Read-only canonical projection for Algolia hits, without touching its index. */
export async function enrichCanonicalSearchResponse<T>(response: T): Promise<T> {
  const envelope = response as { results?: Array<{ hits?: Record<string, unknown>[] }> };
  const hits = envelope.results?.flatMap(result => Array.isArray(result.hits) ? result.hits : []) || [];
  const ids = [...new Set(hits.map(hit => Number(hit.id ?? hit.objectID))
    .filter(id => Number.isSafeInteger(id) && id > 0))];
  if (!ids.length) return response;
  const projections = new Map<string, Record<string, unknown>>();
  const deadline = Date.now() + 2500;
  for (let offset = 0; offset < ids.length; offset += 100) {
    if (Date.now() >= deadline) break;
    const data = await optionalProjection(`${apiBase}/public-canonical/cars?ids=${ids.slice(offset, offset + 100).join(",")}`, deadline - Date.now());
    if (!Array.isArray(data?.cars)) { projectionWarning("batch-missing"); continue; }
    for (const car of data.cars) {
      if (car && typeof car === "object" && ids.includes(Number(car.id))) projections.set(String(car.id), car);
    }
  }
  for (const hit of hits) {
    const car = projections.get(String(hit.id ?? hit.objectID));
    if (!car) { projectionWarning("current-car-projection-missing"); continue; }
    Object.assign(hit, { publicCanonical: car.publicCanonical, canonicalUrl: car.canonicalUrl,
      canonicalPath: car.canonicalPath });
  }
  return response;
}

/** Preserve existing server DOM while preparing the synchronous hydration data. */
export async function prepareCanonicalBootstrap(snapshot: unknown, pathname: string) {
  // Country and legacy snapshots can use the lossless wire envelope. Walking
  // that envelope directly sees only tagged arrays, not the car DTOs inside it.
  snapshot = decodeLandingSeed(snapshot);
  rememberCanonicalPayload(snapshot);
  const missing = new Set<number>();
  function visit(value: unknown, context = "") {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) { value.forEach(child => visit(child, context)); return; }
    const node = value as Record<string, unknown>;
    const id = Number(node.carId ?? node.id);
    const projection = node.publicCanonical as { off?: { path?: string }; on?: { path?: string } } | undefined;
    if (Number.isSafeInteger(id) && id > 0 && (!projection?.off?.path || !projection?.on?.path) &&
        (["car", "cars", "similarCars"].includes(context) || typeof node.carSlug === "string" ||
          ("year" in node && ("slug" in node || "url" in node)))) missing.add(id);
    for (const [key, child] of Object.entries(node)) {
      if (key !== "publicCanonical") visit(child, key);
    }
  }
  visit(snapshot);
  const ids = [...missing];
  const deadline = Date.now() + 2500;
  for (let offset = 0; offset < ids.length; offset += 100) {
    if (Date.now() >= deadline) break;
    const data = await optionalProjection(`${apiBase}/public-canonical/cars?ids=${ids.slice(offset, offset + 100).join(",")}`, deadline - Date.now());
    if (!Array.isArray(data?.cars) || data.cars.length !== ids.slice(offset, offset + 100).length) {
      projectionWarning("bootstrap-partial");
    }
  }
  if (!readCanonicalRoute(pathname)) {
    await optionalProjection(`${apiBase}/public-canonical/page?path=${encodeURIComponent(pathname)}`);
  }
}
